// application/useCases/loadAuthorizationSession.js

/**
 * Reads everything the authorization editor needs for one client.
 *
 * `asOf` RESOLVES EACH PAYER TO THE VERSION THAT APPLIES, and is not optional. Funding sources are
 * effective-dated: a plain read returns one document per VERSION, so without a date a payer that has
 * ever been amended silently vanishes from the picker. It is also why the funders cannot be passed
 * in from a store — the session owns the as-of, and anything reading without one disagrees with it.
 *
 * A CLIENT THAT DOES NOT EXIST YET HAS NO AUTHORIZATIONS, but the payers still load: a letter can be
 * recorded while creating a client, and the flush stamps the id afterwards.
 *
 * @param {{ clientId: string|null, clinicId: string, asOf: string }} scope
 * @param {Object} repositories
 * @param {import('../ports/AuthorizationRepository.port').AuthorizationRepository}
 *   repositories.authorizationRepository
 * @param {import('../ports/FundingSourceRepository.port').FundingSourceRepository}
 *   repositories.fundingSourceRepository
 * @returns {Promise<{
 *   authorizations: import('../../domain/model/Authorization.types').Authorization[],
 *   fundingSources: Object[],
 * }>}
 */
// eslint-disable-next-line import/prefer-default-export
export async function loadAuthorizationSession(
  { clientId, clinicId, asOf },
  { authorizationRepository, fundingSourceRepository },
) {
  const hasClient = clientId !== null && clientId !== undefined;

  const [fundingSources, authorizations] = await Promise.all([
    fundingSourceRepository.loadFundingSources(clinicId, asOf),
    hasClient
      ? authorizationRepository.loadClientAuthorizations({ clientId, clinicId })
      : Promise.resolve([]),
  ]);

  return { authorizations, fundingSources };
} // loadAuthorizationSession()
